import { useCallback, useState } from 'react';
import { LayoutChangeEvent, useWindowDimensions } from 'react-native';
import { SharedValue, runOnJS, useAnimatedReaction, useSharedValue } from 'react-native-reanimated';

type VisibleRows = { first: number; last: number };

export const useVisibleArcadeRows = (scrollOffset: SharedValue<number>) => {
    const { height: viewportHeight } = useWindowDimensions();
    const listOffset = useSharedValue(0);
    const rowHeight = useSharedValue(0);
    const [visibleRows, setVisibleRows] = useState<VisibleRows | null>(null);

    useAnimatedReaction(
        () => {
            if (listOffset.value === 0 || rowHeight.value === 0) {
                return null;
            }
            const top = scrollOffset.value - listOffset.value;
            return {
                first: Math.floor(top / rowHeight.value),
                last: Math.floor((top + viewportHeight) / rowHeight.value),
            };
        },
        (rows, previous) => {
            if (rows && (rows.first !== previous?.first || rows.last !== previous?.last)) {
                runOnJS(setVisibleRows)(rows);
            }
        },
        [viewportHeight]
    );

    // y is in scroll content coordinates only while the list is a direct child of the lobby ScrollView
    const onListLayout = useCallback(
        (event: LayoutChangeEvent) => {
            listOffset.value = event.nativeEvent.layout.y;
        },
        [listOffset]
    );

    const onFirstCellLayout = useCallback(
        (event: LayoutChangeEvent) => {
            rowHeight.value = event.nativeEvent.layout.height;
        },
        [rowHeight]
    );

    const isRowVisible = useCallback(
        (row: number) => visibleRows !== null && row >= visibleRows.first && row <= visibleRows.last,
        [visibleRows]
    );

    return { isRowVisible, onListLayout, onFirstCellLayout };
};
